import React, { useState } from 'react';
import {
    Box, Paper, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert,
    Table, TableBody, TableRow, TableCell,
} from '@mui/material';
import { PowerSettingsNew, PlayArrow, Warning, CheckCircle, Info, Refresh } from '@mui/icons-material';
import {
    inviteDriversOnline, previewInviteDrivers, getInviteResult,
    bulkActivateDrivers, previewBulkActivation,
    AppTypeName, InvitePreview, InviteOnlineResponse, InviteResult, InviteSkipped,
    BulkActivatePreview, BulkActivateResponse, BulkSkipped,
} from '../../services/cityOperationService';

// L2 (driver reach, 2026-10-07): "Invite drivers to go online" + plan-safe Bulk Activation.
// Both are preview → confirm (exact numbers) → result; neither changes anyone's online status.

export interface PlanOption { id: number; name: string; interval: string }

interface Props {
    city: string;
    appType: AppTypeName;
    testPhoneNumber: string;
    plans: PlanOption[];
    scopeLabel: string;
    onTestRunDone?: () => void;
}

export const INVITE_TITLE_MAX = 65;
export const INVITE_BODY_MAX = 240;

export const inviteSkipLabels = (minIntervalHours?: number): Record<keyof InviteSkipped, string> => ({
    cooldown: `Already invited in the last ${minIntervalHours ?? 6} h`,
    busy: 'On a ride or holding a booking',
    notNow: 'Tapped "Not now" recently',
    quietHours: 'Quiet hours',
    noToken: 'No driver-app push token',
    nullAppType: 'App type unknown (old account)',
    suspended: 'Suspended',
    notOffline: 'Already marked online / silent',
    cannotAccept: 'Wallet below the minimum — can\'t take rides',
    cityDailyCap: 'Over today\'s city invite cap',
});

export const BULK_SKIP_LABELS: Record<keyof BulkSkipped, string> = {
    hasActivePlan: 'Already have an active plan (left untouched)',
    suspended: 'Suspended',
    noToken: 'No driver-app push token',
    nullAppType: 'App type unknown (old account)',
};

const VALIDITY: Record<string, string> = { daily: '1 day', weekly: '1 week', monthly: '1 month', yearly: '1 year' };
export const validityLabel = (interval: string) => VALIDITY[interval] ?? interval;
export const fmtIstDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
export const phoneTail = (p: string) => `••${String(p).replace(/\D/g, '').slice(-4)}`;
const errMsg = (e: any, fallback: string) => e?.response?.data?.message || fallback;
export const OLD_BACKEND = 'Server is on an older version — deploy the backend first.';
// The preview endpoints are new: a 404 there means the backend predates them.
const previewErr = (e: any, fallback: string) => (e?.response?.status === 404 ? OLD_BACKEND : errMsg(e, fallback));
// Counts read defensively: an older backend answers with forcedCount / activatedCount only.
export const invitedCount = (d?: Partial<InviteOnlineResponse> | null): number => d?.invited ?? d?.forcedCount ?? 0;
export const grantedCount = (d?: Partial<BulkActivateResponse> | null): number => d?.granted ?? d?.activatedCount ?? 0;

const SkipTable: React.FC<{ rows: Array<[string, number]> }> = ({ rows }) => (
    <Table size="small" sx={{ mt: 1 }}>
        <TableBody>
            {rows.map(([label, n]) => (
                <TableRow key={label}>
                    <TableCell sx={{ py: 0.5 }}>{label}</TableCell>
                    <TableCell sx={{ py: 0.5 }} align="right">{n}</TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

export const inviteSkipRows = (s?: Partial<InviteSkipped> | null, hours?: number): Array<[string, number]> => {
    const labels = inviteSkipLabels(hours);
    return (Object.keys(labels) as Array<keyof InviteSkipped>).map((k) => [labels[k], s?.[k] ?? 0]);
};
export const bulkSkipRows = (s?: Partial<BulkSkipped> | null): Array<[string, number]> =>
    (Object.keys(BULK_SKIP_LABELS) as Array<keyof BulkSkipped>).map((k) => [BULK_SKIP_LABELS[k], s?.[k] ?? 0]);

const DriverReachActions: React.FC<Props> = ({ city, appType, testPhoneNumber, plans, scopeLabel, onTestRunDone }) => {
    const testPhone = testPhoneNumber.trim();
    const contextReady = !!city && !!appType;
    const countsLine = testPhone
        ? 'Counts: the one driver with the test phone, found by phone alone (city and app type are ignored).'
        : `Counts: drivers whose current or home city is ${city || '(pick a city)'}, with app type ${appType}.`;

    // --- invite ---
    const [copy, setCopy] = useState({ title: '', body: '' });
    const [invitePreview, setInvitePreview] = useState<InvitePreview | null>(null);
    const [inviteConfirm, setInviteConfirm] = useState(false);
    const [inviteBusy, setInviteBusy] = useState(false);
    const [inviteOutcome, setInviteOutcome] = useState<{ ok: boolean; message: string; data?: Partial<InviteOnlineResponse> } | null>(null);
    const [online, setOnline] = useState<{ busy: boolean; data: InviteResult | null; error: string | null }>({ busy: false, data: null, error: null });

    const handleInvitePreview = async () => {
        setInvitePreview(null);
        if (testPhone) { setInviteConfirm(true); return; }
        setInviteBusy(true);
        try {
            const res = await previewInviteDrivers(city, appType);
            setInvitePreview(res.data);
            setInviteConfirm(true);
        } catch (e: any) {
            setInviteOutcome({ ok: false, message: previewErr(e, 'Failed to preview invites') });
        } finally { setInviteBusy(false); }
    };

    const handleInviteSend = async () => {
        setInviteConfirm(false);
        setInviteBusy(true);
        setOnline({ busy: false, data: null, error: null });
        try {
            const res = await inviteDriversOnline({
                city, appType,
                testPhoneNumber: testPhone || undefined,
                title: copy.title.trim() || undefined,
                body: copy.body.trim() || undefined,
            });
            setInviteOutcome({ ok: true, message: res.data?.message || '', data: res.data ?? {} });
            if (testPhone) onTestRunDone?.();
        } catch (e: any) {
            const d = e?.response?.data;
            setInviteOutcome({ ok: false, message: errMsg(e, 'Failed to send invites'), data: d?.skipped ? d : undefined });
        } finally { setInviteBusy(false); }
    };

    const handleCheckOnline = async (batchId: string) => {
        setOnline({ busy: true, data: null, error: null });
        try {
            const res = await getInviteResult(batchId);
            setOnline({ busy: false, data: res.data, error: null });
        } catch (e: any) {
            setOnline({ busy: false, data: null, error: errMsg(e, 'Could not read the result') });
        }
    };

    // --- bulk activation ---
    const [planId, setPlanId] = useState<number | ''>('');
    const [bulkPreview, setBulkPreview] = useState<BulkActivatePreview | null>(null);
    const [bulkConfirm, setBulkConfirm] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkOutcome, setBulkOutcome] = useState<{ ok: boolean; message: string; data?: BulkActivateResponse } | null>(null);
    const selectedPlan = plans.find((p) => p.id === planId) || null;

    // The button is disabled until a plan is picked.
    const handleBulkPreview = async () => {
        setBulkPreview(null);
        if (testPhone) { setBulkConfirm(true); return; }
        setBulkBusy(true);
        try {
            const res = await previewBulkActivation(city, appType, Number(planId));
            setBulkPreview(res.data);
            setBulkConfirm(true);
        } catch (e: any) {
            setBulkOutcome({ ok: false, message: previewErr(e, 'Failed to preview the grant') });
        } finally { setBulkBusy(false); }
    };

    const handleBulkConfirm = async () => {
        setBulkConfirm(false);
        setBulkBusy(true);
        try {
            const res = await bulkActivateDrivers({ city, appType, planId: Number(planId), testPhoneNumber: testPhone || undefined });
            setBulkOutcome({ ok: true, message: res.data?.message || '', data: res.data ?? ({} as BulkActivateResponse) });
            if (testPhone) onTestRunDone?.();
        } catch (e: any) {
            setBulkOutcome({ ok: false, message: errMsg(e, 'Failed to grant the plan') });
        } finally { setBulkBusy(false); }
    };

    const inviteSent = invitedCount(inviteOutcome?.data) > 0;
    const bulkGranted = grantedCount(bulkOutcome?.data) > 0;
    const inviteOld = !!inviteOutcome?.ok && inviteOutcome.data?.invited == null;
    const bulkOld = !!bulkOutcome?.ok && bulkOutcome.data?.granted == null;
    const canInviteNow = !!testPhone || (!!invitePreview && !invitePreview.quietHoursActive && invitePreview.wouldInvite > 0);
    const inviteCount = testPhone ? 1 : invitePreview?.wouldInvite ?? 0;
    const canGrantNow = !!testPhone || (!!bulkPreview && bulkPreview.eligible > 0);
    const shownCopy = { title: copy.title.trim() || '🚕 Ready to drive?', body: copy.body.trim() || `Go online in the app to start receiving ride requests in ${testPhone ? 'your city' : city || 'your city'}.` };

    return (
        <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                {/* Invite drivers to go online */}
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <PowerSettingsNew /> Invite drivers to go online
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Sends a "go online" notification to <strong>offline</strong> drivers. It never marks anyone online — a driver
                        counts as online only when their own app goes online. Busy, suspended and recently-invited drivers are
                        skipped; quiet hours apply. Acts on <strong>{scopeLabel}</strong>.
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: -1.5, mb: 2 }} data-testid="invite-counts">{countsLine}</Typography>
                    <TextField
                        size="small" label="Title (optional)" value={copy.title} sx={{ mb: 1.5 }}
                        placeholder="🚕 Ready to drive?"
                        inputProps={{ maxLength: INVITE_TITLE_MAX }}
                        onChange={(e) => setCopy((c) => ({ ...c, title: e.target.value }))}
                    />
                    <TextField
                        size="small" label="Message (optional)" value={copy.body} multiline minRows={2} sx={{ mb: 2 }}
                        placeholder="Go online in the app to start receiving ride requests in your city."
                        helperText="Keep it honest: never claim riders are waiting unless they are."
                        inputProps={{ maxLength: INVITE_BODY_MAX }}
                        onChange={(e) => setCopy((c) => ({ ...c, body: e.target.value }))}
                    />
                    <Button
                        variant="outlined" color="warning"
                        onClick={handleInvitePreview}
                        disabled={inviteBusy || (!testPhone && !contextReady)}
                        endIcon={inviteBusy ? <CircularProgress size={18} color="inherit" /> : <PowerSettingsNew />}
                        sx={{ borderRadius: 2, alignSelf: 'flex-start', mt: 'auto' }}
                    >
                        {inviteBusy ? 'Working…' : 'Preview invite'}
                    </Button>
                </Paper>

                {/* Bulk Driver Activation */}
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <PlayArrow /> Bulk Driver Activation
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Grants a plan, free, <strong>only to drivers with no active plan</strong>. Existing plans (paid or free) are
                        never expired, replaced or shortened; suspended drivers and drivers without a driver-app push token get no
                        plan; nobody's online status changes. Acts on <strong>{scopeLabel}</strong>.
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: -1.5, mb: 2 }} data-testid="bulk-counts">{countsLine}</Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="bulk-plan-label">Grant Plan</InputLabel>
                        <Select labelId="bulk-plan-label" value={planId} label="Grant Plan" onChange={(e) => setPlanId(e.target.value as number)}>
                            {plans.map((p) => (
                                <MenuItem key={p.id} value={p.id}>{p.name} (ID: {p.id} - {p.interval})</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        onClick={handleBulkPreview}
                        disabled={bulkBusy || !planId || (!testPhone && !contextReady)}
                        endIcon={bulkBusy ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
                        sx={{ borderRadius: 2, alignSelf: 'flex-start', mt: 'auto' }}
                    >
                        {bulkBusy ? 'Working…' : 'Preview grant'}
                    </Button>
                </Paper>
            </Box>

            {/* --- Invite: confirm with exact numbers --- */}
            <Dialog open={inviteConfirm} onClose={() => setInviteConfirm(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Warning color="warning" /> Send go-online invites?</DialogTitle>
                <DialogContent>
                    {testPhone ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>TEST:</strong> one invite to the driver with phone {phoneTail(testPhone)}, found by phone alone (city and
                            app type are ignored). Quiet hours and the per-driver interval do not apply to the test phone.
                        </Alert>
                    ) : invitePreview && (
                        <>
                            {invitePreview.quietHoursActive && (
                                <Alert severity="warning" sx={{ mb: 2 }}>Quiet hours are on — invites cannot be sent now.</Alert>
                            )}
                            <Typography gutterBottom>
                                <strong>{invitePreview.wouldInvite}</strong> offline driver(s) in <strong>{invitePreview.city} · {invitePreview.appType}</strong> will
                                get a "go online" notification.
                            </Typography>
                            {invitePreview.deferredOverCap > 0 && (
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    {invitePreview.deferredOverCap} more are next in line (max {invitePreview.cap} per click, least-recently-invited first).
                                </Typography>
                            )}
                            {invitePreview.cityDayCap != null && (
                                <Typography variant="body2" color="textSecondary" gutterBottom data-testid="invite-city-cap">
                                    City cap: at most {invitePreview.cityDayCap} invites per city per day (INVITE_MAX_PER_CITY_DAY).
                                </Typography>
                            )}
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>Not invited</Typography>
                            <SkipTable rows={inviteSkipRows(invitePreview.skipped, invitePreview.minIntervalHours)} />
                        </>
                    )}
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                        Nobody's status changes. Notification: <strong>{shownCopy.title}</strong> — {shownCopy.body}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setInviteConfirm(false)}>Cancel</Button>
                    <Button onClick={handleInviteSend} variant="contained" disabled={!canInviteNow}>
                        Send to {inviteCount} driver(s)
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- Invite: result --- */}
            <Dialog open={!!inviteOutcome} onClose={() => setInviteOutcome(null)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {inviteOutcome?.ok ? (inviteSent ? <CheckCircle color="success" /> : <Info color="warning" />) : <Info color="error" />}
                    {inviteOutcome?.ok ? `Invited ${invitedCount(inviteOutcome.data)} driver(s)` : 'Invites not sent'}
                </DialogTitle>
                <DialogContent>
                    <Alert severity={inviteOutcome?.ok ? (inviteSent ? 'success' : 'warning') : 'error'} sx={{ mb: 1 }}>{inviteOutcome?.message}</Alert>
                    {inviteOld && <Alert severity="warning" sx={{ mb: 1 }}>{OLD_BACKEND}</Alert>}
                    {inviteOutcome?.ok && inviteOutcome.data?.fcm && (
                        <Typography variant="body2" gutterBottom>
                            Notifications sent to Firebase: {inviteOutcome.data.fcm.accepted}
                            {inviteOutcome.data.fcm.failed ? ` · failed: ${inviteOutcome.data.fcm.failed}` : ''}
                            {' '}(Firebase accepting a push does not prove the phone showed it.)
                        </Typography>
                    )}
                    {inviteOutcome?.data?.skipped && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>Not invited</Typography>
                            <SkipTable rows={inviteSkipRows(inviteOutcome.data.skipped, inviteOutcome.data.minIntervalHours)} />
                        </>
                    )}
                    {inviteOutcome?.ok && inviteOutcome.data?.batchId && (
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="outlined" size="small"
                                onClick={() => handleCheckOnline(inviteOutcome.data!.batchId as string)}
                                disabled={online.busy}
                                startIcon={online.busy ? <CircularProgress size={14} color="inherit" /> : <Refresh />}
                            >
                                Check who came online
                            </Button>
                            {online.data && (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    {online.data.cameOnline} of {online.data.invited} invited driver(s) are online now
                                    ({online.data.minutesSinceSent} min after sending). Only drivers whose own app is online count.
                                </Alert>
                            )}
                            {online.error && <Alert severity="error" sx={{ mt: 1 }}>{online.error}</Alert>}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={() => setInviteOutcome(null)} variant="contained">Close</Button></DialogActions>
            </Dialog>

            {/* --- Bulk: confirm with exact numbers --- */}
            <Dialog open={bulkConfirm} onClose={() => setBulkConfirm(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Warning color="warning" /> Confirm plan grant</DialogTitle>
                <DialogContent>
                    {testPhone ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>TEST:</strong> grants <strong>{selectedPlan?.name}</strong> ({validityLabel(selectedPlan?.interval ?? '')}) to the
                            driver with phone {phoneTail(testPhone)}, found by phone alone (city and app type are ignored) — only if they
                            have no active plan.
                        </Alert>
                    ) : bulkPreview && (
                        <>
                            {bulkPreview.plan && (
                                <Typography gutterBottom>
                                    Plan: <strong>{bulkPreview.plan.name}</strong> — valid {bulkPreview.plan.validity ?? validityLabel(bulkPreview.plan.interval)} (a grant
                                    made now ends {fmtIstDate(bulkPreview.plan.endDate)}).
                                </Typography>
                            )}
                            <Typography gutterBottom>
                                <strong>{bulkPreview.eligible ?? 0}</strong> driver(s) in <strong>{city} · {appType}</strong> have no active plan and
                                will get it free (wallet not charged).
                            </Typography>
                            {bulkPreview.quietHoursActive && (
                                <Alert severity="info" sx={{ mb: 1 }}>Quiet hours are on: plans are granted, but no notification is sent.</Alert>
                            )}
                            {bulkPreview.truncated && (
                                <Alert severity="warning" sx={{ mb: 1 }}>The count stopped at its safety limit — the real number may be higher.</Alert>
                            )}
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>Not granted</Typography>
                            <SkipTable rows={bulkSkipRows(bulkPreview.skipped)} />
                        </>
                    )}
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                        Existing plans are never expired, replaced or shortened. Nobody's online status changes.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setBulkConfirm(false)}>Cancel</Button>
                    <Button onClick={handleBulkConfirm} variant="contained" disabled={!canGrantNow}>
                        Grant to {testPhone ? 1 : bulkPreview?.eligible ?? 0} driver(s)
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- Bulk: result --- */}
            <Dialog open={!!bulkOutcome} onClose={() => setBulkOutcome(null)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {bulkOutcome?.ok ? (bulkGranted ? <CheckCircle color="success" /> : <Info color="warning" />) : <Info color="error" />}
                    {bulkOutcome?.ok ? `Granted to ${grantedCount(bulkOutcome.data)} driver(s)` : 'Plan not granted'}
                </DialogTitle>
                <DialogContent>
                    <Alert severity={bulkOutcome?.ok ? (bulkGranted ? 'success' : 'warning') : 'error'} sx={{ mb: 1 }}>{bulkOutcome?.message}</Alert>
                    {bulkOld && <Alert severity="warning" sx={{ mb: 1 }}>{OLD_BACKEND}</Alert>}
                    {bulkOutcome?.ok && bulkOutcome.data && (
                        <>
                            {bulkOutcome.data.pushed != null && (
                                <Typography variant="body2" gutterBottom>
                                    Notifications sent to Firebase: {bulkOutcome.data.pushed}
                                    {bulkOutcome.data.pushSkippedQuietHours ? ' (quiet hours — none sent)' : ''}
                                </Typography>
                            )}
                            {(bulkOutcome.data.pushSkippedCannotAccept ?? 0) > 0 && (
                                <Typography variant="body2" gutterBottom>
                                    {bulkOutcome.data.pushSkippedCannotAccept} got the plan, no push — wallet below the minimum.
                                </Typography>
                            )}
                            {bulkOutcome.data.skipped && (
                                <>
                                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Not granted</Typography>
                                    <SkipTable rows={bulkSkipRows(bulkOutcome.data.skipped)} />
                                </>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={() => setBulkOutcome(null)} variant="contained">Close</Button></DialogActions>
            </Dialog>
        </>
    );
};

export default DriverReachActions;
